<script setup lang="ts">
import { useSerialize } from "@lxc/app-device-common";
import type { InternalRuleItem } from "async-validator/dist-types/interface";
import dayjs from "dayjs";
import { storeToRefs } from "pinia";
import type LxcError from "~/core/models/LxcError.class";
import type { OperationModel } from "~/core/models/OperationModel.interface";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useCreateOperationRenewCertificate } from "~/modules/dtwin/composables/useCreateOperationRenewCertificate";
import { useUpdateOperationRenewCertificate } from "~/modules/dtwin/composables/useUpdateOperationRenewCertificate";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { DtwinOperation } from "~/modules/dtwin/models/DtwinOperation.interface";
import type { DtwinOperationCertificateInputParameters } from "~/modules/dtwin/models/DtwinOperationCertificateInputParameters.interface";
import { OperationCreationMode } from "~/modules/dtwin/models/OperationCreationMode.enum";
import { OperationScheduler } from "~/modules/dtwin/models/OperationScheduler.enum";
import { OperationServiceOrigin } from "~/modules/dtwin/models/OperationServiceOrigin.enum";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";

const props = defineProps<{
  show: boolean;
  dtwin?: Dtwin;
  dtwinOperationModel?: OperationModel;
  operation?: DtwinOperation<DtwinOperationCertificateInputParameters>;
}>();

const emit = defineEmits(["update:show", "change"]);

const { t } = useI18n();

const serialize = useSerialize();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const {
  create,
  error: errorCreate,
  isLoading: isLoadingCreate,
} = useCreateOperationRenewCertificate({
  organizationCode: currentOrganization.value?.code,
});

const {
  update,
  error: errorUpdate,
  isLoading: isLoadingUpdate,
} = useUpdateOperationRenewCertificate({
  organizationCode: currentOrganization.value?.code,
});

const rules = {
  launchPlannedAt: [
    {
      required: true,
      message: t("operation.certificate.update.dateTimeError"),
    },
    {
      validator: (
        _: InternalRuleItem,
        value: Date,
        callback: (error?: Error) => void,
      ) => {
        if (
          value.getTime() >
          dayjs(props.dtwin?.features.certificate.reported.notAfter).valueOf()
        ) {
          callback(
            new Error(
              t("operation.certificate.update.dateNotValidAfterRenewal"),
            ),
          );
        } else {
          callback();
        }
      },
    },
  ],
};

const operationFormRef = ref();
const operationFormModel = ref();
const operationFormModelSerialized = ref();

const showCanvas = computed({
  get() {
    return props.show;
  },
  set(show) {
    emit("update:show", show);
  },
});

const disabledButtonValidate = computed(
  () =>
    operationFormModelSerialized.value ===
      serialize(operationFormModel.value) ||
    !operationFormRef.value?.isValid ||
    isLoadingCreate.value ||
    isLoadingUpdate.value,
);

function setOperationForm(
  operation?: DtwinOperation<DtwinOperationCertificateInputParameters>,
) {
  operationFormModel.value = {
    uid: operation?.uid,
    launchPlannedAt: operation?.launchPlannedAt
      ? new Date(operation.launchPlannedAt)
      : undefined,
  };

  operationFormModelSerialized.value = serialize(operationFormModel.value);

  setTimeout(operationFormRef.value?.clearValidate, 0);
}

async function onSubmit() {
  if (
    props.dtwin &&
    props.dtwinOperationModel &&
    (await operationFormRef.value?.validate().catch(() => false))
  ) {
    const error: Ref<LxcError | undefined> = ref();

    const operation = {
      launchPlannedAt: dayjs(operationFormModel.value?.launchPlannedAt)
        .utc()
        .toISOString(),
    };

    // Update operation
    if (operationFormModel.value.uid) {
      await update(operationFormModel.value.uid, operation);

      if (errorUpdate.value) {
        error.value = errorUpdate.value;
      }
    }
    // Create operation
    else {
      await create({
        modelUid: props.dtwinOperationModel.uid,
        deviceTwinUid: props.dtwin.uid,
        serviceOrigin: OperationServiceOrigin.UNITARY,
        creationMode: OperationCreationMode.MANUAL,
        expiredAt: dayjs(operationFormModel.value?.launchPlannedAt)
          .add(1, "month")
          .utc()
          .toISOString(),
        scheduler: OperationScheduler.SERVICE,
        startsAtCreation: props.dtwinOperationModel.hasInternalScheduler,
        ...operation,
      });

      if (errorCreate.value) {
        error.value = errorCreate.value;
      }
    }

    if (error.value) {
      error.value.notify(NotificationKey.error);
    } else {
      showNotificationSuccess(t(NotificationKey.success));

      showCanvas.value = false;

      setOperationForm();

      emit("change");
    }
  }
}

watch(
  () => props.show,
  (show) => {
    setOperationForm(show ? props.operation : undefined);
  },
);
</script>

<template>
  <lxc-side-canvas
    v-model:show="showCanvas"
    :header="t('operation.certificate.update.label')"
    size="1/3"
  >
    <lxc-form
      v-if="operationFormModel"
      ref="operationFormRef"
      :rules="rules"
      :model="operationFormModel"
    >
      <lxc-form-item
        prop="launchPlannedAt"
        :label="t('operation.certificate.update.plannedStartAt.label')"
      >
        <lxc-input
          v-model="operationFormModel.launchPlannedAt"
          type="datetime"
          :placeholder="
            t('operation.certificate.update.plannedStartAt.placeholder')
          "
        />
      </lxc-form-item>
    </lxc-form>

    <template #footer>
      <div class="grid grid-cols-[max-content_auto] gap-4">
        <lxc-button
          html-type="button"
          type="secondary"
          :title="t('button.cancel')"
          @click="showCanvas = false"
        >
          {{ t("button.cancel") }}
        </lxc-button>

        <lxc-button
          html-type="button"
          :disabled="disabledButtonValidate"
          :is-loading="isLoadingCreate || isLoadingUpdate"
          :title="t('button.validate')"
          @click="onSubmit"
        >
          {{ t("button.validate") }}
        </lxc-button>
      </div>
    </template>
  </lxc-side-canvas>
</template>
